import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@nbx/frontend-helpers/subscribe';
import { capitalizeWord } from 'helpers/strings';
import CandlesticksDatafeedAdaptor from './CandlesticksDatafeedAdaptor';
import styles from './styles.module.scss';

const containerID = 'tvchart_container_id';

function TradingViewChart(props) {
  const chartWidgetRef = useRef(null);
  const theme = useTheme();

  useEffect(
    () => {
      const overrides = {
        // General
        'paneProperties.background': theme === 'dark' ? '#000' : '#fff',
        'scalesProperties.backgroundColor': theme === 'dark' ? '#000' : '#fff',
        // Candlesticks
        'mainSeriesProperties.candleStyle.upColor': '#30CE82',
        'mainSeriesProperties.candleStyle.downColor': '#ee4852',
        'mainSeriesProperties.candleStyle.borderColor': '#378658',
        'mainSeriesProperties.candleStyle.borderUpColor': '#30CE82',
        'mainSeriesProperties.candleStyle.borderDownColor': '#ee4852',
        'mainSeriesProperties.candleStyle.wickUpColor': '#30CE82',
        'mainSeriesProperties.candleStyle.wickDownColor': '#ee4852',
        // Hollow Candles
        'mainSeriesProperties.hollowCandleStyle.upColor': '#30CE82',
        'mainSeriesProperties.hollowCandleStyle.downColor': '#ee4852',
        'mainSeriesProperties.hollowCandleStyle.borderColor': '#378658',
        'mainSeriesProperties.hollowCandleStyle.borderUpColor': '#30CE82',
        'mainSeriesProperties.hollowCandleStyle.borderDownColor': '#ee4852',
        // Heikin Ashi
        'mainSeriesProperties.haStyle.upColor': '#30CE82',
        'mainSeriesProperties.haStyle.downColor': '#ee4852',
        'mainSeriesProperties.haStyle.borderUpColor': '#30CE82',
        'mainSeriesProperties.haStyle.borderDownColor': '#ee4852',
        // Bar Styles
        'mainSeriesProperties.barStyle.upColor': '#30CE82',
        'mainSeriesProperties.barStyle.downColor': '#ee4852',
        // Line Styles
        'mainSeriesProperties.lineStyle.color': theme === 'dark' ? '#30CE82' : '#1D3EE9',
        // Area Styles
        'mainSeriesProperties.areaStyle.color1': theme === 'dark' ? '#30CE82' : '#1D3EE9',
        'mainSeriesProperties.areaStyle.color2': theme === 'dark' ? '#30CE82' : '#1D3EE9',
        'mainSeriesProperties.areaStyle.linecolor': theme === 'dark' ? '#30CE82' : '#1D3EE9',
        // Baseline Styles
        'mainSeriesProperties.baselineStyle.baselineColor': '#30CE82',
        'mainSeriesProperties.baselineStyle.topFillColor1': '#30CE82',
        'mainSeriesProperties.baselineStyle.topFillColor2': '#30CE82',
        'mainSeriesProperties.baselineStyle.bottomFillColor1': '#ee4852',
        'mainSeriesProperties.baselineStyle.bottomFillColor2': '#ee4852',
        'mainSeriesProperties.baselineStyle.topLineColor': '#30CE82',
        'mainSeriesProperties.baselineStyle.bottomLineColor': '#ee4852'
      };

      if (chartWidgetRef.current !== null) {
        chartWidgetRef.current.changeTheme(capitalizeWord(theme));
        chartWidgetRef.current.applyOverrides(overrides);
      } else if (theme !== undefined) {
        const widgetOptions = {
          symbol: props.pair,
          datafeed: CandlesticksDatafeedAdaptor(props.market, props.refreshRate),
          container: containerID,
          library_path: '/assets/trading-view/',
          custom_css_url: '/assets/trading-view/tv_styles.css',
          loading_screen: {
            backgroundColour: theme === 'dark' ? '#30CE82' : '#FFF',
            foregroundColour: theme === 'dark' ? '#30CE82' : '#1D3EE9'
          },
          overrides,
          locale: 'en',
          timezone: 'Etc/UTC',
          theme: capitalizeWord(theme),
          disabled_features: [
            'header_symbol_search',
            'header_symbol_search_hot_key',
            'header_compare',
            'compare_symbol',
            'header_saveload',
            'timeframes_toolbar'
          ],
          autosize: true
        };

        // eslint-disable-next-line new-cap
        const chartWidget = new window.TradingView.widget(widgetOptions);

        setTimeout(() => {
          chartWidgetRef.current = chartWidget;
        });
      }

      return () => {
        if (chartWidgetRef.current !== null) {
          chartWidgetRef.current.remove();
          chartWidgetRef.current = null;
        }
      };
    },
    [theme] // eslint-disable-line
  );

  return <div className={styles.tradingView} id={containerID} />;
}

TradingViewChart.propTypes = {
  pair: PropTypes.string.isRequired,
  market: PropTypes.object,
  refreshRate: PropTypes.number.isRequired
};

TradingViewChart.defaultProps = {
  market: undefined
};

export default TradingViewChart;
